import { Color, Spacing } from '@persona-id/design-tokens';
import { noop } from 'lodash';
import { FC, Fragment, useState } from 'react';
import styled from 'styled-components';

import { PlatformJsonSchema } from 'dashboard/lib/workflows/platform-object';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconCaret from 'images/icons/caret-down.svg';

const HorizontalScrollableBox = styled.div`
  font-family: ${({ theme }) => theme.text.fontFamily.mono};
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  font-weight: ${FontWeight.Medium};
  padding: ${Spacing[4]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  overflow-x: auto;
  cursor: ${({ onClick }) => (onClick === noop ? 'default' : 'pointer')};
`;

const Row = styled.div`
  display: flex;
  padding: ${BASE / 4}px 0;
`;

const ExpandButton = styled.div`
  display: flex;
  padding-right: ${Spacing[4]}; // Extra space when scrolling to right edge
  align-items: center;
  align-self: baseline;
  cursor: pointer;
  &:focus {
    color: ${Color.CorePurple800};
    & rec[fill],
    & path[fill] {
      fill: ${Color.CorePurple800};
    }
  }
`;

const StyledIconCaret = styled(({ expanded, menuItem, ...props }) => <IconCaret {...props} />)<{
  expanded: boolean;
}>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '0deg' : '-90deg')});
  justify-self: center;
  margin-right: ${Spacing[3]};

  rect[fill],
  path[fill] {
    fill: ${({ theme }) => theme.color.text.primary};
  }
`;

const Key = styled.div`
  white-space: nowrap;
`;

const Value = styled.div`
  white-space: nowrap;
  margin-left: ${Spacing[2]};
  padding-right: ${Spacing[4]}; // Extra space when scrolling to right edge
`;

const Nest = styled.div`
  margin-left: ${Spacing[6]};
`;

interface JsonSchemaPreviewProps {
  id?: string;
  jsonSchema: Record<string, PlatformJsonSchema>;
  requiredFields?: string[];
  onClick?: () => void;
}

const JsonSchemaPreview: FC<JsonSchemaPreviewProps> = ({
  id,
  jsonSchema,
  requiredFields = [],
  onClick = noop,
}) => {
  return (
    <HorizontalScrollableBox
      id={id || 'json-schema-preview__scrollbox'}
      role='tree'
      onClick={onClick}
    >
      {Object.entries(jsonSchema).length === 0 ? (
        'No Data'
      ) : (
        <JsonSchemaPreviewRow jsonSchema={jsonSchema} requiredFields={requiredFields} />
      )}
    </HorizontalScrollableBox>
  );
};

const JsonSchemaPreviewRow: FC<{
  jsonSchema: Record<string, PlatformJsonSchema> | PlatformJsonSchema;
  requiredFields: string[];
  depth?: number;
}> = ({ jsonSchema, requiredFields = [], depth = 0 }) => {
  const [expandedIndexes, updateExpandedIndexes] = useState<number[]>(
    depth < 2 ? Object.keys(jsonSchema).map((_key, i) => i) : [],
  );
  const toggleExpansion = (index: number) => {
    updateExpandedIndexes((currentExpandedIndexes) =>
      currentExpandedIndexes.includes(index)
        ? currentExpandedIndexes.filter((i) => i !== index)
        : [...currentExpandedIndexes, index],
    );
  };
  const getRequiredFields = (
    jsonSchema: Record<string, PlatformJsonSchema> | PlatformJsonSchema,
  ): string[] => {
    if ('required' in jsonSchema) {
      return jsonSchema['required'] as string[];
    }
    return [];
  };
  return (
    <>
      {Object.entries(jsonSchema)
        .filter(([key, _]) => key !== 'id' && key !== 'required')
        .map(([key, value], index) => (
          <Fragment key={`${index}-${depth}`}>
            <Row>
              {typeof value === 'object' && (
                <ExpandButton
                  id={`json-schema-row__expand-object-${key}-${index}-${depth}`}
                  role='button'
                  aria-label={
                    expandedIndexes.includes(index) ? 'Hide nested fields' : 'Show nested fields'
                  }
                  aria-expanded={expandedIndexes.includes(index)}
                  tabIndex={0}
                  onClick={(e) => {
                    toggleExpansion(index);
                    e.stopPropagation();
                  }}
                  onKeyPress={(e) => {
                    if (['Enter', 'Space'].includes(e.nativeEvent.code)) {
                      toggleExpansion(index);
                      e.preventDefault();
                    }
                  }}
                >
                  <StyledIconCaret expanded={expandedIndexes.includes(index)} />
                  <Key>{key}:</Key>
                </ExpandButton>
              )}
              {typeof value !== 'object' && (
                <>
                  <Key>{key}:</Key>
                  <Value>{value}</Value>
                </>
              )}
            </Row>
            {typeof value === 'object' && expandedIndexes.includes(index) && (
              <Nest>
                {requiredFields && requiredFields.includes(key) && (
                  <Row>
                    <Key>required:</Key>
                    <Value>true</Value>
                  </Row>
                )}
                <JsonSchemaPreviewRow
                  jsonSchema={value}
                  requiredFields={key === 'properties' ? getRequiredFields(jsonSchema) : []}
                  depth={depth + 1}
                />
              </Nest>
            )}
          </Fragment>
        ))}
    </>
  );
};

export default JsonSchemaPreview;
